import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { MoodPicker } from '../components/MoodPicker';
import { TopicTags } from '../components/TopicTags';
import { SafetyNet } from '../components/SafetyNet';
import { SafetyQuestion } from '../components/SafetyQuestion';
import { HelpStrip } from '../components/HelpStrip';
import { CheckInDeck } from '../components/CheckInDeck';
import { Lockup } from '../ui/Lockup';
import { LANGS, useI18n, type Strings } from '../i18n';
import { STRINGS } from '../i18n/strings';
import { I18nContext } from '../i18n/context';
import { STUDENT_LANGS, STUDENT_STRINGS, type StudentLang } from '../i18n/studentStrings';
import { useDocumentTitle } from '../ui/a11y';
import { useFocusHeadingOnNavigate } from '../ui/focus';

type SessionInfo = {
  session_id: string;
  class_label: string;
  school_name: string;
  expires_at: string;
  safety_net?: { fi?: string; sv?: string; en?: string } | null;
  /** The session's question set topics (0017). */
  topics?: string[] | null;
  /** "You said, we did": the teacher's latest line to the class (0017). */
  you_said?: string | null;
  /** The head's latest school-wide line (0018). */
  school_said?: string | null;
  /** The check-in's extra student languages (J10, 0018; per check-in since 0020). */
  languages?: string[] | null;
  /** Names, direction and translation status of those languages (0020). */
  language_info?: ExtraLang[] | null;
  /** "You said, we did": the actions, newest first, the first post last (0020). */
  you_said_items?: string[] | null;
  you_said_updated?: string | null;
  /** The card-deck check-in (0021); false/missing on an older-style check-in. */
  swipe_checkin?: boolean | null;
  /** This class's curated "Something else" follow-up codes (0021), in display order. */
  concern_phrases?: { code: string }[] | null;
  /** Whether this session offers the closing Safety Valve question (0026, spec §14.10). */
  safety_valve?: boolean | null;
};

// A language besides FI/SV/EN. Without a student-screen translation
// (screen_status 'none') the screen stays in English, said plainly.
type ExtraLang = { code: string; native_name: string; dir: 'ltr' | 'rtl'; screen_status: 'reviewed' | 'draft' | 'none' };

type LangState = { extra: ExtraLang | null; setExtra: (l: ExtraLang | null) => void; available: ExtraLang[] };

const hasStrings = (code: string): code is StudentLang => STUDENT_LANGS.some((x) => x.code === code);

// J10: the check-in in a school's extra home languages. Draft translations
// override the student strings; staff screens never change. The page's
// lang and direction follow (Arabic reads right to left).
export function CheckIn() {
  const [extra, setExtra] = useState<ExtraLang | null>(null);
  const [available, setAvailable] = useState<ExtraLang[]>([]);
  return (
    <StudentLangScope extra={extra}>
      <CheckInBody langState={{ extra, setExtra, available }} onLanguages={setAvailable} />
    </StudentLangScope>
  );
}

function StudentLangScope({ extra, children }: { extra: ExtraLang | null; children: ReactNode }) {
  const base = useI18n();
  const translated = extra && hasStrings(extra.code) ? extra.code : null;
  // An extra language without a translation shows the screen in English.
  const untranslated = !!extra && !translated;
  const value = useMemo(
    () =>
      translated
        ? { ...base, t: { ...base.t, ...STUDENT_STRINGS[translated] } as Strings }
        : untranslated
          ? { ...base, t: STRINGS.en as Strings }
          : base,
    [base, translated, untranslated],
  );
  useEffect(() => {
    const el = document.documentElement;
    if (!translated) {
      el.lang = untranslated ? 'en' : base.lang;
      el.dir = 'ltr';
      return;
    }
    el.lang = translated;
    el.dir = extra?.dir ?? 'ltr';
    return () => {
      el.dir = 'ltr';
    };
  }, [extra, translated, untranslated, base.lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

type Status = 'loading' | 'open' | 'expired' | 'submitting' | 'done' | 'error';

// Client-side-only duplicate guard (design doc Section 4.4): a soft flag,
// scoped to this browser tab and this one code, never transmitted to the
// server. It is not an identity — it just saves someone from double-tapping.
function alreadyCheckedInKey(code: string) {
  return `sisuvalo:checked-in:${code}`;
}

function CheckInBody({ langState, onLanguages }: { langState: LangState; onLanguages: (l: ExtraLang[]) => void }) {
  const { code = '' } = useParams<{ code: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [mood, setMood] = useState<number | null>(null);
  const [topics, setTopics] = useState<string[]>([]);
  const [concernTags, setConcernTags] = useState<string[]>([]);
  const [wantsCounsellor, setWantsCounsellor] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadSession() {
      if (sessionStorage.getItem(alreadyCheckedInKey(code))) {
        if (!cancelled) setStatus('done');
        return;
      }

      const { data, error: rpcError } = await supabase
        .rpc('get_active_session', { p_code: code })
        .maybeSingle<SessionInfo>();

      if (cancelled) return;

      if (rpcError || !data) {
        setStatus('expired');
        return;
      }

      setSession(data);
      onLanguages(
        data.language_info ??
          (data.languages ?? []).filter(hasStrings).map((code) => {
            const l = STUDENT_LANGS.find((x) => x.code === code)!;
            return { code, native_name: l.name, dir: l.dir, screen_status: 'draft' as const };
          }),
      );
      setStatus('open');
    }

    loadSession();
    return () => {
      cancelled = true;
    };
  }, [code, onLanguages]);

  async function submit() {
    if (mood === null) return;
    setStatus('submitting');
    setError(null);

    const { error: rpcError } = await supabase.rpc('submit_checkin', {
      p_code: code,
      p_mood: mood,
      p_topics: topics,
      p_concern_tags: concernTags,
      p_wants_counsellor_checkin: wantsCounsellor ?? false,
    });

    if (rpcError) {
      setError('ci.error');
      setStatus('open');
      return;
    }

    sessionStorage.setItem(alreadyCheckedInKey(code), '1');
    setStatus('done');
  }

  const { t, lang, locale } = useI18n();
  const net = session?.safety_net as Record<string, string | undefined> | null | undefined;
  // In an extra language: the school's own text in it, else its Finnish and
  // English texts (never a machine-translated safety message).
  const safety = langState.extra
    ? net?.[langState.extra.code]?.trim() || [net?.fi, net?.en].filter((x) => x?.trim()).join('\n\n') || null
    : net?.[lang] ?? null;
  const replies = session?.you_said_items?.length ? session.you_said_items : session?.you_said ? [session.you_said] : [];

  if (status === 'loading') {
    return (
      <CheckinFrame langState={langState} safety={safety} title={t['ci.pageTitle']}>
        <p className="sx-hint" role="status" style={{ textAlign: 'center' }}>
          {t.loading}
        </p>
      </CheckinFrame>
    );
  }

  if (status === 'expired') {
    return (
      <CheckinFrame langState={langState} safety={safety} title={t['ci.closedTitle']} heading={t['ci.closedTitle']}>
        <div className="sx-done">
          <div className="sx-badge" aria-hidden="true">⏳</div>
          <p>{t['ci.closedBody']}</p>
        </div>
      </CheckinFrame>
    );
  }

  if (status === 'done') {
    return (
      <CheckinFrame langState={langState} safety={safety} title={t['ci.doneTitle']} heading={t['ci.doneTitle']}>
        <div className="sx-done">
          <div className="sx-badge" aria-hidden="true">🌱</div>
          <p>{t['ci.doneBody']}</p>
        </div>
      </CheckinFrame>
    );
  }

  const intro = (
    <>
      {session && (
        <p className="sx-context">{session.class_label} · {session.school_name}</p>
      )}
      {replies.length > 0 ? (
        <figure className="sx-yousaid">
          <figcaption>{t['ci.youSaid']}</figcaption>
          {replies.length === 1 ? (
            <blockquote dir="auto">{replies[0]}</blockquote>
          ) : (
            <ul className="sx-yousaid-list">
              {replies.map((r, i) => (
                <li key={i} dir="auto">{r}</li>
              ))}
            </ul>
          )}
          {session?.you_said_updated && (
            <p className="sx-hint">{t['r4.ci.updated'](new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' }).format(new Date(session.you_said_updated)))}</p>
          )}
        </figure>
      ) : session?.school_said ? (
        <figure className="sx-yousaid">
          <figcaption>{t['ci.schoolSaid']}</figcaption>
          <blockquote dir="auto">{session.school_said}</blockquote>
        </figure>
      ) : null}
    </>
  );

  // Release 4b: a card deck (behind feature:swipe_checkin) instead of the
  // single scrolling card. Older sessions (flag off, or started before this
  // release) keep today's layout exactly as it was.
  if (session?.swipe_checkin) {
    return (
      <CheckinDeckFrame langState={langState} safety={safety} safetyValve={!!session?.safety_valve} title={t['ci.pageTitle']}>
        <CheckInDeck
          intro={intro}
          mood={mood}
          onMood={setMood}
          topics={topics}
          onTopics={setTopics}
          allowedTopics={session?.topics}
          concernTags={concernTags}
          onConcernTags={setConcernTags}
          concernPhrases={(session?.concern_phrases ?? []).map((p) => p.code)}
          safetyValve={!!session?.safety_valve}
          wantsCounsellor={wantsCounsellor}
          onWantsCounsellor={setWantsCounsellor}
          onDone={submit}
          submitting={status === 'submitting'}
          error={error ? t['ci.error'] : null}
        />
      </CheckinDeckFrame>
    );
  }

  const safetyRequired = !!session?.safety_valve && wantsCounsellor === null;

  return (
    <CheckinFrame langState={langState} safety={safety} safetyValve={!!session?.safety_valve} title={t['ci.pageTitle']} heading={t['ci.title']} headingId="ci-title">
      {intro}
      <p className="sx-hint" id="ci-anon">{t['ci.anon']}</p>
      <p className="visually-hidden" id="ci-scale-hint">{t['ci.scaleHint']}</p>

      <MoodPicker value={mood} onChange={setMood} labelledBy="ci-title" describedBy="ci-scale-hint ci-anon" />

      <div className="sx-section">
        <h2 className="sx-question" id="ci-topics">{t['ci.topicsTitle']}</h2>
        <p className="sx-hint" id="ci-topics-hint">{t['ci.topicsHint']}</p>
        <TopicTags value={topics} onChange={setTopics} labelledBy="ci-topics" describedBy="ci-topics-hint" allowed={session?.topics} />
      </div>

      {session?.safety_valve && (
        <div className="sx-section">
          <h2 className="sx-question" id="ci-safety">{t['r6.ci.safetyTitle']}</h2>
          <p className="sx-hint" id="ci-safety-hint">{t['r6.ci.safetyHint']}</p>
          <SafetyQuestion value={wantsCounsellor} onChange={setWantsCounsellor} labelledBy="ci-safety" describedBy="ci-safety-hint" />
        </div>
      )}

      <div className="sx-actions">
        {error && <p className="sx-error" role="alert">{t['ci.error']}</p>}
        <button
          type="button"
          className="sx-btn"
          disabled={mood === null || safetyRequired || status === 'submitting'}
          aria-describedby={mood === null ? 'ci-pick-first' : safetyRequired ? 'ci-safety-pick-first' : undefined}
          onClick={submit}
        >
          {status === 'submitting' ? t['ci.sending'] : t['ci.done']}
        </button>
        {mood === null && <p className="sx-hint sx-center" id="ci-pick-first">{t['ci.pickFirst']}</p>}
        {mood !== null && safetyRequired && <p className="sx-hint sx-center" id="ci-safety-pick-first">{t['r6.ci.safetyPickFirst']}</p>}
      </div>
    </CheckinFrame>
  );
}

// Student surface: lavender-mint ground, one centred glass card with the
// language switch, the safety net always underneath it (on every state, per
// the design doc), and the accessibility statement link.
function CheckinFrame({
  langState,
  title,
  heading,
  headingId,
  children,
  safety,
  safetyValve,
}: {
  langState: LangState;
  safety: string | null;
  safetyValve?: boolean;
  title: string;
  heading?: string;
  headingId?: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const h1 = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(title);
  useFocusHeadingOnNavigate(h1);
  return (
    <div className="sx-ground">
      <div className="sx-page">
        <header className="sx-top">
          <Lockup className="sx-brand" />
          <StudentLangToggle {...langState} />
        </header>
        {langState.extra && langState.extra.screen_status === 'draft' && <p className="sx-draft">{t['ci.draftTranslation']}</p>}
        {langState.extra && !hasStrings(langState.extra.code) && <p className="sx-draft">{t['r4.ci.notTranslated']}</p>}
        <main className="sx-card">
          {heading && (
            <h1 className="sx-title" id={headingId} ref={h1} tabIndex={-1}>
              {heading}
            </h1>
          )}
          {children}
        </main>
        <SafetyNet message={safety} safetyValve={safetyValve} />
        <footer className="sx-foot">
          <Link to="/accessibility">{t['a11y.link']}</Link>
        </footer>
      </div>
    </div>
  );
}

// Release 4b's frame for the card-deck check-in: the same shell as
// CheckinFrame (header, language switch, draft-translation notices, footer),
// but with the help strip promoted above the card — sticky, so it stays
// visible across every step — instead of once below it, and no owned h1
// (CheckInDeck renders its own per-step heading in its place).
function CheckinDeckFrame({
  langState,
  title,
  children,
  safety,
  safetyValve,
}: {
  langState: LangState;
  safety: string | null;
  safetyValve?: boolean;
  title: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  useDocumentTitle(title);
  return (
    <div className="sx-ground">
      <div className="sx-page">
        <header className="sx-top">
          <Lockup className="sx-brand" />
          <StudentLangToggle {...langState} />
        </header>
        {langState.extra && langState.extra.screen_status === 'draft' && <p className="sx-draft">{t['ci.draftTranslation']}</p>}
        {langState.extra && !hasStrings(langState.extra.code) && <p className="sx-draft">{t['r4.ci.notTranslated']}</p>}
        <div className="sx-helpstrip-wrap">
          <HelpStrip message={safety} safetyValve={safetyValve} />
        </div>
        <main className="sx-card">{children}</main>
        <footer className="sx-foot">
          <Link to="/accessibility">{t['a11y.link']}</Link>
        </footer>
      </div>
    </div>
  );
}

// EN / FI / SV plus the school's extra languages, one switch.
function StudentLangToggle({ extra, setExtra, available }: LangState) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className="lang" role="group" aria-label={t['lang.label']}>
      {LANGS.map((l) => (
        <button key={l.code} type="button" lang={l.code} aria-label={l.name} aria-pressed={!extra && lang === l.code}
          className={!extra && lang === l.code ? 'on' : undefined}
          onClick={() => { setExtra(null); setLang(l.code); }}>
          {l.code.toUpperCase()}
        </button>
      ))}
      {available.map((l) => (
        <button key={l.code} type="button" lang={l.code} aria-label={l.native_name} aria-pressed={extra?.code === l.code}
          className={extra?.code === l.code ? 'on' : undefined} onClick={() => setExtra(l)}>
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
